/* Milano density guide — progressive enhancement only.
   1. The "Which density is right for me?" chooser: real radio groups; each
      answer votes for a density tier; when every question is answered the
      matching pre-rendered result card is shown (two on a tie). Without
      JavaScript nothing depends on this file — the comparison cards above
      the chooser carry the same information.
   2. Staggered reveal of cards as they enter the viewport (mcg pattern).
   Idempotent: the script tag may be included by several sections; every
   init is guarded. Theme-editor safe via shopify:section:load/unload. */
(function () {
  'use strict';

  if (window.MilanoDensityGuide) return;
  window.MilanoDensityGuide = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Clarity is the only live analytics sink — always guarded, always silent. */
  function track(name) {
    try {
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch (err) {
      /* fail silent */
    }
  }

  /* ---------- Card reveals ---------- */

  function initReveals(root) {
    var items = (root || document).querySelectorAll('[data-mdc-reveal]:not(.is-revealed)');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(items, function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );

    Array.prototype.forEach.call(items, function (el) {
      var index = Array.prototype.indexOf.call(el.parentNode.children, el);
      el.style.setProperty('--mdc-delay', Math.min(Math.max(index, 0) * 55, 330) + 'ms');
      observer.observe(el);
    });
  }

  /* ---------- Density chooser ---------- */

  function initChooser(form) {
    if (!form || form.dataset.mdcChooserInit) return;
    form.dataset.mdcChooserInit = '1';

    var section = form.closest('[data-section-id]') || document;
    var questions = Array.prototype.slice.call(form.querySelectorAll('[data-mdc-q]'));
    var results = section.querySelector('[data-mdc-results]');
    var status = section.querySelector('[data-mdc-status]');
    var progress = section.querySelector('[data-mdc-progress]');
    var tieNote = section.querySelector('[data-mdc-tie]');
    var reset = section.querySelector('[data-mdc-reset]');
    if (!questions.length || !results) return;

    var cards = {};
    Array.prototype.forEach.call(results.querySelectorAll('[data-mdc-result]'), function (card) {
      cards[card.getAttribute('data-mdc-result')] = card;
    });

    var tierNames = {
      low: results.getAttribute('data-name-low') || 'Low density',
      medium: results.getAttribute('data-name-medium') || 'Medium density',
      high: results.getAttribute('data-name-high') || 'High density'
    };
    var announcedFor = '';

    function answered() {
      return questions.filter(function (q) {
        return q.querySelector('input:checked');
      });
    }

    function hideAll() {
      Object.keys(cards).forEach(function (key) {
        cards[key].hidden = true;
      });
      if (tieNote) tieNote.hidden = true;
      results.hidden = true;
      results.classList.remove('mdc-results--tie');
    }

    function update() {
      var done = answered();

      if (progress) {
        progress.textContent = done.length + ' of ' + questions.length + ' answered';
      }

      if (done.length < questions.length) {
        hideAll();
        announcedFor = '';
        if (status) status.textContent = '';
        return;
      }

      var votes = { low: 0, medium: 0, high: 0 };
      done.forEach(function (q) {
        var tier = q.querySelector('input:checked').getAttribute('data-tier');
        if (votes.hasOwnProperty(tier)) votes[tier] += 1;
      });

      var top = Math.max(votes.low, votes.medium, votes.high);
      var winners = ['low', 'medium', 'high'].filter(function (tier) {
        return votes[tier] === top && cards[tier];
      });
      if (!winners.length) return;

      hideAll();
      results.hidden = false;
      if (winners.length > 1) {
        results.classList.add('mdc-results--tie');
        if (tieNote) tieNote.hidden = false;
      }
      winners.forEach(function (tier) {
        cards[tier].hidden = false;
      });

      var summary =
        winners.length > 1
          ? 'You are between ' + tierNames[winners[0]] + ' and ' + tierNames[winners[1]] + '. Details below.'
          : 'Your match: ' + tierNames[winners[0]] + '. Details below.';
      var key = winners.join('-');
      if (status && announcedFor !== key) {
        status.textContent = summary;
        announcedFor = key;
        track('density_chooser_result_' + key);
      }
    }

    form.addEventListener('change', function (event) {
      if (event.target && event.target.matches('input[type="radio"]')) update();
    });

    if (reset) {
      reset.addEventListener('click', function () {
        Array.prototype.forEach.call(form.querySelectorAll('input:checked'), function (input) {
          input.checked = false;
        });
        hideAll();
        announcedFor = '';
        if (status) status.textContent = '';
        if (progress) progress.textContent = '0 of ' + questions.length + ' answered';
        track('density_chooser_reset');
      });
    }

    results.addEventListener('click', function (event) {
      if (event.target && event.target.closest('a.mdc-btn')) track('density_result_cta');
    });

    /* Answers may survive bfcache restores — reflect them. */
    update();
  }

  function initAll(scope) {
    initReveals(scope || document);
    Array.prototype.forEach.call((scope || document).querySelectorAll('form[data-mdc-chooser]'), initChooser);
  }

  /* Enables the pre-reveal hidden state only when JS is actually running. */
  document.documentElement.classList.add('mdc-js');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
